// Загрузка страницы анкеты /tutors/$slug (ru, az, en) — общий loader.
//
// Старый slug (анкету переименовали или slug починили) → 301 на текущий
// slug того же языка: getPublicPartner находит анкету по хвосту slug
// (начало id, src/lib/partner-slug.ts) и отдаёт movedTo. На сервере
// redirect со statusCode 301 становится ответом 301 с Location.
//
// Найденная анкета уходит в данные loader — их TanStack Start передаёт в
// браузер, поэтому сервер рисует профиль сразу, а гидратация совпадает
// (react-query сам по себе с сервера в браузер ничего не переносит).
import { notFound, redirect } from "@tanstack/react-router";

import { TUTORS_PATH } from "./map-copy";
import type { SiteLang } from "@/components/site/SiteChrome";
import type { SubjectLoad } from "./TutorSubjectPage";
import { getPublicPartner, listFeaturedPartnerIds, listPublicPartners } from "@/lib/map-public.functions";
import { parseMapQuery, type MapQuery } from "./map-query";
import { SUBJECT_PAGE_MIN, isTutorSubject, subjectPageCodes } from "@/lib/tutor-subject-pages";
import { nearbyPartners, slimNearby, type Nearby } from "@/lib/tutor-nearby";

export type PartnerLoad = Awaited<ReturnType<typeof getPublicPartner>>;

export const partnerQueryKey = (slug: string) => ["map-partner", slug] as const;

/** Адрес, на который уводит старый slug: тот же языковой префикс, новый slug. */
export const movedPartnerHref = (lang: SiteLang, movedTo: string) =>
  `${TUTORS_PATH[lang]}/${movedTo}`;

export async function loadPartnerPage(
  lang: SiteLang,
  slug: string,
): Promise<{ initial: PartnerLoad | null; nearby: Nearby | null; pageCodes: string[] }> {
  let res: PartnerLoad;
  try {
    res = await getPublicPartner({ data: { slug } });
  } catch {
    // Сбой чтения — не ошибка маршрута: страница сама запросит анкету
    // и покажет «не удалось загрузить», как до loader.
    return { initial: null, nearby: null, pageCodes: [] };
  }
  if (!res.row && res.movedTo)
    throw redirect({ href: movedPartnerHref(lang, res.movedTo), statusCode: 301 });
  // Анкеты нет (17.10.2026): ответ 404, а не 200 со словами «не найдена».
  // Страница та же — notFoundComponent маршрута рисует PartnerPage с PARTNER_NOT_FOUND.
  // Сбой чтения сюда не попадает (catch выше): там 200 и повтор в браузере.
  if (!res.row) throw notFound();
  // «Yaxınlıqda başqa repetitorlar» (28.10.2026) — на сервере, чтобы ссылки были в HTML.
  // Сбой списка — профиль без блока, не ошибка страницы.
  const { list } = await loadTutorsHub();
  // Страницы предметов, которые есть (≥ 3 анкет): крошки и ссылки профиля ведут только на них (29.10.2026).
  return { initial: res, nearby: list ? slimNearby(nearbyPartners(res.row, list.rows)) : null, pageCodes: list ? subjectPageCodes(list.rows) : [] };
}

/** Слаг без хвоста из пробелов, точек и знаков препинания. */
export const trimSlugTail = (slug: string) => slug.replace(/[\s.,;:!?'"»)\]]+$/u, "");

/** Куда ведёт очищенный слаг: кабинет и форма — свои страницы, пусто — карта. */
export function cleanTutorsHref(lang: SiteLang, clean: string): string {
  if (!clean) return TUTORS_PATH[lang];
  return `${TUTORS_PATH[lang]}/${encodeURIComponent(clean)}`;
}

/** Данные «анкеты нет» для notFoundComponent: PartnerPage печатает «не найдена». */
export const PARTNER_NOT_FOUND = { row: null, movedTo: null } as PartnerLoad;

export type HubLoad = Awaited<ReturnType<typeof listPublicPartners>>;

/**
 * Список карты для хаба /tutors: сервер рисует карточки, интро и ссылки на
 * предметы, а head — JSON-LD ItemList. Сбой — null: страница запросит список
 * сама, как до loader.
 */
export async function loadTutorsHub(): Promise<{ list: HubLoad | null }> {
  try {
    return { list: await listPublicPartners() };
  } catch {
    return { list: null };
  }
}

/** Перемешать (Фишер — Йейтс): случайный порядок блока «Рекомендуемые» — на сервере. */
function shuffled<T>(a: readonly T[]): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
}

/**
 * Хаб карты /tutors (02.11.2026): список, рекомендуемые и фильтры из адреса.
 * Рекомендуемые — параллельно со списком; их порядок случайный и решается
 * ЗДЕСЬ, на сервере: страница берёт первые до 4, прошедших фильтры и вкладку,
 * и рисует блок уже в SSR (без запроса из браузера и без сдвига макета).
 * Фильтры из адреса (?type=&subject=&district=&city=) — начальное состояние
 * страницы и на сервере, и в браузере. Страницы предметов этот loader не зовут.
 */
export async function loadTutorsMapPage(searchStr: string): Promise<{ list: HubLoad | null; featured: string[]; query: MapQuery }> {
  const [{ list }, ids] = await Promise.all([
    loadTutorsHub(),
    listFeaturedPartnerIds()
      .then((r) => r.ids)
      .catch(() => [] as string[]),
  ]);
  // Только анкеты из списка (опубликованы, не на паузе).
  const inList = new Set((list?.rows ?? []).map((r) => r.id));
  return { list, featured: shuffled(ids.filter((id) => inList.has(id))), query: parseMapQuery(searchStr) };
}

/**
 * /tutors/$slug: код предмета с ≥ SUBJECT_PAGE_MIN анкетами — страница
 * предмета; иначе — профиль анкеты, как раньше (неизвестный slug — «не найдено»).
 */
export async function loadTutorSlug(
  lang: SiteLang,
  slug: string,
): Promise<{ initial: PartnerLoad | null; nearby: Nearby | null; pageCodes: string[]; subject: SubjectLoad | null }> {
  // Хвост из точки, пробела или знака препинания — ссылку скопировали вместе с
  // концом фразы (/tutors/me. из письма): 301 на правильный адрес.
  const clean = trimSlugTail(slug);
  if (clean !== slug) throw redirect({ href: cleanTutorsHref(lang, clean), statusCode: 301 });
  if (isTutorSubject(slug)) {
    const { list } = await loadTutorsHub();
    if (list) {
      const rows = list.rows.filter((r) => r.kind === "tutor" && r.subjects.includes(slug));
      if (rows.length >= SUBJECT_PAGE_MIN) return { initial: null, nearby: null, pageCodes: [], subject: { code: slug, rows, pageCodes: subjectPageCodes(list.rows) } };
    }
  }
  return { ...(await loadPartnerPage(lang, slug)), subject: null };
}
